/**
 * ServiceHistorySection Component
 * Collapsible service history table for a truck
 */

import React from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { CustomDatePicker } from '../../../common/CustomDatePicker';
import { formatServiceDate, formatDateForInput } from '../utils/formatters';
import type { Truck } from '../types';

interface ServiceHistorySectionProps {
  truck: Truck;
  loading: boolean;
  onAddServiceEntry: (truckId: string) => void;
  onUpdateServiceStatus: (truckId: string, entryId: string, currentStatus: string) => void;
  onUpdateServiceDate: (truckId: string, entryId: string, newDate: string) => void;
  onDeleteServiceEntry: (truckId: string, entryId: string) => void;
  canEdit?: boolean;
}

export const ServiceHistorySection: React.FC<ServiceHistorySectionProps> = ({
  truck,
  loading,
  onAddServiceEntry,
  onUpdateServiceStatus,
  onUpdateServiceDate,
  onDeleteServiceEntry,
  canEdit = true,
}) => {
  return (
    <Box sx={{ margin: 2, backgroundColor: '#f9fafb', p: 2, borderRadius: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" gutterBottom component="div" sx={{ fontWeight: 600, fontSize: '1rem' }}>
          Service History {!canEdit && <Typography component="span" sx={{ color: '#6b7280', fontSize: '0.875rem', ml: 1 }}>(Read Only)</Typography>}
        </Typography>
        {canEdit && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => onAddServiceEntry(truck.id)}
            sx={{
              bgcolor: '#10b981',
              '&:hover': {
                bgcolor: '#059669',
              },
            }}
          >
            Add Service
          </Button>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : truck.serviceHistory && truck.serviceHistory.length > 0 ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, width: '200px' }}>Service Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, width: '70px', textAlign: 'right' }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {truck.serviceHistory
              .sort((a, b) => new Date(b.serviceDate).getTime() - new Date(a.serviceDate).getTime())
              .map((entry) => (
              <TableRow key={entry.id}>
                <TableCell>
                  {canEdit ? (
                    <CustomDatePicker
                      value={formatDateForInput(entry.serviceDate)}
                      onChange={(value) => onUpdateServiceDate(truck.id, entry.id, value)}
                      showYear={true}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          fontSize: '0.875rem',
                          backgroundColor: '#ffffff',
                        },
                      }}
                    />
                  ) : (
                    <Typography sx={{ fontSize: '0.875rem', color: '#1f2937' }}>
                      {formatServiceDate(entry.serviceDate)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={entry.status.toUpperCase()}
                    onClick={canEdit ? () => onUpdateServiceStatus(truck.id, entry.id, entry.status) : undefined}
                    sx={{
                      backgroundColor: entry.status === 'pass' ? '#10b981' : '#ef4444',
                      color: 'white',
                      fontWeight: 600,
                      cursor: canEdit ? 'pointer' : 'default',
                      '&:hover': canEdit ? {
                        backgroundColor: entry.status === 'pass' ? '#059669' : '#dc2626',
                      } : {},
                    }}
                  />
                </TableCell>
                <TableCell sx={{ textAlign: 'right' }}>
                  {canEdit && (
                    <IconButton
                      size="small"
                      onClick={() => onDeleteServiceEntry(truck.id, entry.id)}
                      sx={{
                        color: '#ef4444',
                        '&:hover': {
                          backgroundColor: '#fee2e2',
                        },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" sx={{ color: '#6b7280', textAlign: 'center', py: 2 }}>
          No service history yet. Click "Add Service" to create the first entry.
        </Typography>
      )}
    </Box>
  );
};
